import { History } from 'history';
import { Dataset, DatasetControl, GridRow } from '../../../../types/types';
import useRoutes from '../../../useRoutes';
import { useControlActionModalContent } from '../../../useControlActionModalContent';
import {
  requestReportTemplates,
  StoredReportTemplateSummary,
} from '../../../../api/requests/reportTemplates/requestReportTemplates';
import { listReportTemplates } from '../../../../pages/DatasetPrint/utils/resolveReportTemplate';
import { SubsetPrintState } from '../../../../pages/DatasetPrint/types';

export type SubsetPrintNavigationContext = {
  parentDataset: Dataset;
  parentRow: GridRow;
  parentIdItem: string;
  subsetId: string;
  returnPath: string;
};

/**
 * Opens the print menu for the current view. Editing a report is a separate
 * `type: 'app'` control and therefore always uses the single app route.
 */
export const getPrintTypeCallback = (
  control: DatasetControl,
  dataset: Dataset,
  activeFilters: Record<string, unknown> | null,
  activeSorting: { field: string; order: number }[],
  token: string | null,
  routes: ReturnType<typeof useRoutes>,
  history: History,
  openPrintTemplateModal: ReturnType<
    typeof useControlActionModalContent
  >['openPrintTemplateModal'],
  close: () => void,
  setModalLoading: (loading: boolean) => void,
  subsetPrintContext?: SubsetPrintNavigationContext
) => {
  return async () => {
    const datasetId = String(dataset.datasetId ?? '');
    let stored: StoredReportTemplateSummary[] = [];
    let hasTemplateListError = false;

    try {
      setModalLoading(true);
      const response = await requestReportTemplates(datasetId, token);
      stored = response.items ?? [];
    } catch {
      hasTemplateListError = true;
    } finally {
      setModalLoading(false);
    }

    openPrintTemplateModal(
      control.label,
      listReportTemplates(dataset, stored),
      (templateId: string) => {
        close();
        if (!subsetPrintContext) {
          history.push(routes.datasetPrint(datasetId, templateId));
          return;
        }

        const parentDatasetId = String(
          subsetPrintContext.parentDataset.datasetId ?? ''
        );
        const parentId = String(
          subsetPrintContext.parentRow[subsetPrintContext.parentIdItem] ?? ''
        );
        const subsetPrint: SubsetPrintState = {
          subsetId: subsetPrintContext.subsetId,
          parentRow: subsetPrintContext.parentRow,
          parentId,
          parentIdItem: subsetPrintContext.parentIdItem,
          activeFilters,
          activeSorting,
          returnPath: subsetPrintContext.returnPath,
        };
        history.push(routes.datasetPrint(parentDatasetId, templateId), {
          subsetPrint,
        });
      },
      undefined,
      undefined,
      undefined,
      undefined,
      hasTemplateListError
    );
  };
};
